import type {
  TimedMetadataItem,
  UpdateTimedMetadataRequestDto,
} from '@mimir/common/dto/timedMetadataModels';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { AxiosError } from 'axios';
import { computed, reactive } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import { createUuid } from '@mimir/common/util/createUuid';
import { getRestrictedFormUsageRules } from '@mimir/web/util/usageRestrictionsCheck';
import { getUsageRuleFromTimedMdf } from '@mimir/common/timed-mdf/usage-rules';
import { applyIngestTimeIfUsageRuleExists, getIngestTimeFromItem } from '@mimir/common/util/timedMetadata';
import { getTimedMetadata, updateTimedMetadata } from '@mimir/web/util/api';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { showToast, TOAST } from '@mimir/web/eventBus';
import { abortable, isAbortError } from '@mimir/common/util/abortControl';
import TimelineItem from '@mimir/web/model/timeline/TimelineItem';
import Vue from 'vue';
import {
  dtoToModel,
  mergeRemoteIntoLocal,
  type AddTimedMdfPayload,
  type CopyTimedMdfPayload,
  type SaveTimedMdfItemsPayload,
  type SetSelectedTimedMdfItemPayload,
  type TimedMdf,
  type TimedMdfOps,
  type TimedMdfServices,
  type TimedMdfState,
  type TimedMetadataItemModel,
} from '../model/timedMdf';

// Internal source-of-truth fields that consumers must not couple to.
// See wiki: State & Reactivity Conventions, rule 5.
interface TimedMdfStatePrivate extends TimedMdfState {
  eventsSnapshot: TimedMetadataItemModel[];
  selectedTimedMdf: MetaDataSchema | null;
  savingItemIds: Set<string>;
}

function removeTimedMdfFromState(
  collectionItems: TimedMetadataItemModel[],
  eventsSnapshot: TimedMetadataItemModel[],
  timedItemId: string,
): void {
  let itemRemovedFromCollection = false;

  for (let i = 0; i < collectionItems.length; i++) {
    if (collectionItems[i].id === timedItemId) {
      collectionItems.splice(i, 1);
      itemRemovedFromCollection = true;
    }

    if (eventsSnapshot[i] && eventsSnapshot[i].id === timedItemId) {
      eventsSnapshot.splice(i, 1);
    }

    if (itemRemovedFromCollection) {
      break;
    }
  }
}

function updateItemChanged(itemsWithChanges: string[], timedItemId: string, hasChanged: boolean): string[] {
  if (!hasChanged) {
    return itemsWithChanges.filter(id => id !== timedItemId);
  }
  const alreadyIn = itemsWithChanges.includes(timedItemId);
  if (!alreadyIn) {
    return [...itemsWithChanges, timedItemId];
  }
  return itemsWithChanges;
}

function modelToDto(model: TimedMetadataItemModel): TimedMetadataItem {
  return {
    id: model.id,
    data: cloneDeep(model.data),
    startMs: model.startMs,
    endMs: model.endMs,
  };
}

function buildTimedMdfUpdate(
  itemsById: Record<string, TimedMetadataItemModel>,
  timedItemIds: string[],
): UpdateTimedMetadataRequestDto['items'] {
  const update: UpdateTimedMetadataRequestDto['items'] = {};
  for (const itemId of timedItemIds) {
    const item = itemsById[itemId];
    if (item?.hasValidationErrors) {
      continue;
    }
    update[itemId] = item ? modelToDto(item) : null;
  }
  return update;
}

export function createTimedMdfStates(services: TimedMdfServices): TimedMdf {
  const state: TimedMdfStatePrivate = reactive({
    timedMdfSaving: false,
    savingItemIds: new Set<string>(),
    collectionItems: [] as TimedMetadataItemModel[],
    eventsSnapshot: [] as TimedMetadataItemModel[],
    selectedItem: null as TimedMetadataItemModel | null,
    hoveredItem: null as TimedMetadataItemModel | null,
    selectedTimedMdf: null as MetaDataSchema | null,
    loadingCollection: false,
    loadedCollection: false,
    itemsWithChanges: [] as string[],
    shouldAutoFocusSelectedItem: true,
    timedMdfs: computed(() => services.getTimedMdfs()),
    remoteUpdateCount: 0,
  });

  function setItemHasChanged(timedItemId: string, hasChanged: boolean): void {
    state.itemsWithChanges = updateItemChanged(
      state.itemsWithChanges,
      timedItemId,
      hasChanged,
    );
  }

  function markItemsSaved(itemsById: Record<string, TimedMetadataItemModel>, timedItemIds: string[]): void {
    const mdfs = services.getTimedMdfs();
    const usageRules = getRestrictedFormUsageRules(mdfs);
    const currentItemForIngestTime = services.getCurrentItem();
    timedItemIds.forEach(timedItemId => {
      setItemHasChanged(timedItemId, false);
      const item = itemsById[timedItemId];
      if (item) {
        item.isNewNonSavedItem = false;
        if (usageRules && currentItemForIngestTime) {
          Vue.set(item, 'usageRule', getUsageRuleFromTimedMdf(item, usageRules));
          applyIngestTimeIfUsageRuleExists(item, getIngestTimeFromItem(currentItemForIngestTime));
        }
      }
    });
  }

  function setSelectedItemInternal(payload: SetSelectedTimedMdfItemPayload): void {
    state.selectedItem = payload.item as TimedMetadataItemModel | null;
    state.shouldAutoFocusSelectedItem = payload.shouldAutoFocusSelectedItem;
    if (payload.item !== null && payload.setStart) {
      payload.playerOps.seekToVirtualClipMediaPosition({ approximate: payload.item.startMs / 1000.0 });
    }
  }

  const ops: TimedMdfOps = {
    async loadTimedMdf(): Promise<void> {
      const currentItem = services.getCurrentItem();
      if (currentItem === null) {
        return;
      }
      if (state.loadedCollection) {
        return;
      }

      const itemId = currentItem.id;
      state.loadingCollection = true;
      try {
        const collection = await getTimedMetadata(itemId);
        if (services.getCurrentItem()?.id !== itemId) {
          return;
        }
        const mdfs = services.getTimedMdfs();
        const usageRules = getRestrictedFormUsageRules(mdfs);
        const items = collection.items.map(it => dtoToModel(it, usageRules, currentItem.timecode));
        state.collectionItems = items;
        state.eventsSnapshot = cloneDeep(items);
        state.selectedItem = null;
        if (mdfs.length > 0) {
          state.selectedTimedMdf = mdfs[0];
        }
        state.loadedCollection = true;
      } catch (err) {
        if (services.getCurrentItem()?.id !== itemId) {
          return;
        }
        logErrorAndShowToast('Failed loading log events, please try again later', err, 'ex in loadTimedMdf');
      } finally {
        state.loadingCollection = false;
      }
    },

    async loadForItem(itemId: string): Promise<void> {
      state.loadingCollection = true;
      try {
        const collection = await getTimedMetadata(itemId);
        const mdfs = services.getTimedMdfs();
        const usageRules = getRestrictedFormUsageRules(mdfs);
        const items = collection.items.map(it => dtoToModel(it, usageRules, undefined));
        state.collectionItems = items;
        state.eventsSnapshot = cloneDeep(items);
        state.loadedCollection = true;
      } catch (err) {
        logErrorAndShowToast('Failed loading log events, please try again later', err, 'ex in loadForItem');
      } finally {
        state.loadingCollection = false;
      }
    },

    cleanup(): void {
      state.selectedItem = null;
      state.collectionItems = [];
      state.eventsSnapshot = [];
      state.timedMdfSaving = false;
      state.savingItemIds = new Set();
      state.itemsWithChanges = [];
      state.loadingCollection = false;
      state.loadedCollection = false;
      state.remoteUpdateCount = 0;
    },

    setSelectedItem(payload: SetSelectedTimedMdfItemPayload): void {
      setSelectedItemInternal(payload);
    },

    setHovered(item: TimedMetadataItemModel | null): void {
      state.hoveredItem = item;
    },

    setShouldAutoFocusSelectedItem(value: boolean): void {
      state.shouldAutoFocusSelectedItem = value;
    },

    setSelectedItemHasErrors(hasErrors: boolean): void {
      if (state.selectedItem) {
        state.selectedItem.hasValidationErrors = hasErrors;
      }
    },

    add(info: AddTimedMdfPayload): void {
      const timeline = services.getTimeline();
      const virtualClipDurationInMs =
        info.playerOps.getVirtualClipDurationObs().currentValue().approximate * 1000.0;

      let timeRange = timeline.getTimeRange(
        virtualClipDurationInMs,
        info.playerOps.getVirtualClipStartObs().currentValue(),
      );
      let setStart = true;

      console.log(`timeRange = ${JSON.stringify(timeRange)}`);

      if (timeRange === null) {
        const currentTime = info.playerOps.getVirtualClipMediaPositionObs().currentValue().approximate * 1000.0;
        const logEventDuration = (info.mdf.duration ?? 5) * 1000;

        let start = currentTime;
        let end = Math.min(currentTime + logEventDuration, virtualClipDurationInMs);
        const minLogEventDuration = 1000;

        if ((end - minLogEventDuration) <= currentTime) {
          start = Math.max(0, virtualClipDurationInMs - minLogEventDuration);
          end = virtualClipDurationInMs;
        }

        timeRange = { start, end };
        setStart = false;
      } else {
        const end = Math.max(timeRange.start + TimelineItem.MIN_DURATION, timeRange.end);
        timeRange = {
          start: timeRange.start,
          end,
        };
      }

      const payload: ItemMetaData = {
        formId: info.mdf.id,
        formData: {},
      };
      const errors = info.mdf.sanitize(services.getOptionLists(), payload, false);
      const newTimedMdfItem: TimedMetadataItemModel = {
        id: createUuid(),
        swimlane: 0,
        hasValidationErrors: !isEmpty(errors),
        isNewNonSavedItem: true,
        startMs: timeRange.start,
        endMs: timeRange.end,
        data: payload,
        timecode: info.timecode,
        selected: true,
        creationInfo: {
          source: 'api',
          date: new Date().toISOString(),
          userId: services.getCurrentUserId(),
        },
      };
      state.collectionItems.push(newTimedMdfItem);
      if (newTimedMdfItem.isNewNonSavedItem) {
        state.eventsSnapshot.push(newTimedMdfItem);
      }
      setItemHasChanged(newTimedMdfItem.id, true);
      services.clearTimelineState();
      setSelectedItemInternal({
        playerOps: info.playerOps,
        item: newTimedMdfItem,
        setStart,
        shouldAutoFocusSelectedItem: true,
      });
    },

    addItem(item: TimedMetadataItemModel): void {
      state.collectionItems.push(item);
      if (item.isNewNonSavedItem) {
        state.eventsSnapshot.push(item);
      }
      setItemHasChanged(item.id, true);
    },

    remove(timedItem: TimedMetadataItemModel): void {
      removeTimedMdfFromState(
        state.collectionItems,
        state.eventsSnapshot,
        timedItem.id,
      );
      const requiresSave = !timedItem.isNewNonSavedItem;
      setItemHasChanged(timedItem.id, requiresSave);
      if (state.selectedItem?.id === timedItem.id) {
        state.selectedItem = null;
      }
    },

    copyTimedMdf(payload: CopyTimedMdfPayload): void {
      const { oldTimedMdfItem, playerOps } = payload;
      const currentTime = playerOps.getVirtualClipMediaPositionObs().currentValue().approximate * 1000.0;
      const virtualClipDurationInMs = playerOps.getVirtualClipDurationObs().currentValue().approximate * 1000.0;
      let endMs = (oldTimedMdfItem.endMs - oldTimedMdfItem.startMs) + currentTime;
      if (endMs > virtualClipDurationInMs) {
        endMs = virtualClipDurationInMs;
      }

      const timedMdfs = services.getTimedMdfs();
      const timedMdf = timedMdfs.filter(mdf => mdf.id === oldTimedMdfItem.data.formId)[0] as MetaDataSchema;
      const errors = timedMdf.sanitize(services.getOptionLists(), oldTimedMdfItem.data, false);

      const newTimedMdfItem: TimedMetadataItemModel = {
        ...oldTimedMdfItem,
        startMs: currentTime,
        endMs,
        id: createUuid(),
        swimlane: 0,
        hasValidationErrors: !isEmpty(errors),
        isNewNonSavedItem: true,
        creationInfo: {
          source: 'api',
          date: new Date().toISOString(),
          userId: services.getCurrentUserId(),
        },
      };
      state.collectionItems.push(newTimedMdfItem);
      if (newTimedMdfItem.isNewNonSavedItem) {
        state.eventsSnapshot.push(newTimedMdfItem);
      }
      setItemHasChanged(newTimedMdfItem.id, true);
      services.clearTimelineState();
      setSelectedItemInternal({
        playerOps,
        item: newTimedMdfItem,
        setStart: true,
        shouldAutoFocusSelectedItem: false,
      });
    },

    itemHasChanged(timedItemId: string, hasChanged: boolean): void {
      setItemHasChanged(timedItemId, hasChanged);
    },

    updateItemsWithSnapshot(): void {
      const itemsWithChanges = state.itemsWithChanges;
      const snapshot = state.eventsSnapshot;
      const items = state.collectionItems;

      const selectedItem = state.selectedItem;
      if (selectedItem?.isNewNonSavedItem) {
        ops.remove(selectedItem);
      }

      itemsWithChanges.forEach(id => {
        const snapshotItem = snapshot.find(item => item.id === id);
        if (snapshotItem) {
          const itemToUpdate = items.find(item => item.id === id);
          if (itemToUpdate) {
            Object.assign(itemToUpdate, snapshotItem);
          }
        }
      });
      state.selectedItem = null;
      state.itemsWithChanges = [];
    },

    getSnapshotEntry(timedItemId: string): TimedMetadataItemModel | undefined {
      return state.eventsSnapshot.find(e => e.id === timedItemId);
    },

    async saveItems(payload: SaveTimedMdfItemsPayload): Promise<void> {
      const { timedItemIds, abortController } = payload;
      state.timedMdfSaving = true;
      state.savingItemIds = new Set(timedItemIds);
      try {
        const itemsById = keyBy(state.collectionItems, it => it.id);
        const update = buildTimedMdfUpdate(itemsById, timedItemIds);
        if (isEmpty(update)) {
          return;
        }
        const request: UpdateTimedMetadataRequestDto = {
          items: update,
        };
        const itemId = services.getCurrentItem()?.id;
        if (!itemId) {
          throw new Error('Expected current item to be present');
        }

        await abortable(
          async () => {
            await updateTimedMetadata(itemId, request);
          },
          abortController.signal,
        );

        markItemsSaved(itemsById, timedItemIds);
        state.eventsSnapshot = cloneDeep(state.collectionItems);
        showToast('Log events updated', TOAST.SUCCESS);
      } catch (err) {
        if (isAbortError(err)) {
          throw err;
        }

        if ((err as AxiosError).response?.status === 400) {
          logErrorAndShowToast('Please fix all validation errors in all log events', err, 'ex in saveItems');
        } else {
          logErrorAndShowToast('Failed updating log events, please try again later', err, 'ex in saveItems');
        }
      } finally {
        state.timedMdfSaving = false;
        state.savingItemIds = new Set();
      }
    },

    setSelectedTimedMdf(mdf: MetaDataSchema | null): void {
      state.selectedTimedMdf = mdf;
    },

    setLoadedCollection(loaded: boolean): void {
      state.loadedCollection = loaded;
    },

    applyRemoteDelete(id: string): void {
      removeTimedMdfFromState(
        state.collectionItems,
        state.eventsSnapshot,
        id,
      );
    },

    applyFullRemoteEntry(id: string, fetched: TimedMetadataItem): void {
      const items = state.collectionItems;
      const idx = items.findIndex(e => e.id === id);
      if (idx === -1) {
        const usageRules = getRestrictedFormUsageRules(services.getTimedMdfs());
        const model = dtoToModel(fetched, usageRules, services.getCurrentItem()?.timecode);
        items.push(model);
        state.eventsSnapshot.push(cloneDeep(model));
        return;
      }

      const updated: TimedMetadataItemModel = {
        ...items[idx],
        data: cloneDeep(fetched.data),
        generated_summary: fetched.generated_summary,
        startMs: fetched.startMs,
        endMs: fetched.endMs,
        lastModified: fetched.lastModified ?? items[idx].lastModified,
      };
      items.splice(idx, 1, updated);
      if (state.selectedItem?.id === id) {
        state.selectedItem = updated;
      }

      // The snapshot gets its own copy of the remote data so later local
      // edits to the live entry cannot corrupt the base used for merges.
      const snapshotEntry: TimedMetadataItemModel = { ...updated, data: cloneDeep(fetched.data) };
      const snapshots = state.eventsSnapshot;
      const snapIdx = snapshots.findIndex(e => e.id === id);
      if (snapIdx === -1) {
        snapshots.push(snapshotEntry);
      } else {
        snapshots.splice(snapIdx, 1, snapshotEntry);
      }
    },

    mergeRemoteEntry(id: string, fetched: TimedMetadataItem): void {
      const base = state.eventsSnapshot.find(e => e.id === id);
      const local = state.collectionItems.find(e => e.id === id);
      if (base === undefined || local === undefined) {
        // No base available for a three-way merge, apply full remote entry.
        ops.applyFullRemoteEntry(id, fetched);
        return;
      }

      const merged = mergeRemoteIntoLocal(base, local, fetched);
      const items = state.collectionItems;
      const idx = items.findIndex(e => e.id === id);
      if (idx !== -1) {
        items.splice(idx, 1, merged);
        if (state.selectedItem?.id === id) {
          state.selectedItem = merged;
        }
      }

      // The snapshot tracks the latest server state, not the merged result.
      const snapshots = state.eventsSnapshot;
      const snapIdx = snapshots.findIndex(e => e.id === id);
      if (snapIdx !== -1) {
        snapshots.splice(snapIdx, 1, {
          ...snapshots[snapIdx],
          data: cloneDeep(fetched.data),
          generated_summary: fetched.generated_summary,
          startMs: fetched.startMs,
          endMs: fetched.endMs,
          lastModified: fetched.lastModified ?? snapshots[snapIdx].lastModified,
        });
      }
    },

    incrementRemoteUpdateCount(): void {
      state.remoteUpdateCount++;
    },
  };

  return { state, ops };
}
